import {
	UndoOutlined,
	ZoomInOutlined,
	ZoomOutOutlined,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ShortcutLabel } from "components/ShortcutLabel/ShortcutLabel"
import { Tooltip } from "components/Tooltip/Tooltip"
import { WeightIcon } from "icons/WeightIcon"
import type {
	ComponentProps,
	HTMLProps,
	MouseEvent,
	ReactElement,
	ReactNode,
	TouchEvent,
	TouchList,
	WheelEvent,
} from "react"
import {
	Children,
	cloneElement,
	isValidElement,
	useEffect,
	useMemo,
	useState,
} from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { cn } from "styles/cn"
import { Image } from "./Image"

export const ZoomableImage = ({
	src,
	children,
	className,
	imgClassName,
	toolbarClassName,
	toolbarLeft,
	toolbarRight,
	errorMessage,
	onZoomUpdate,
	onLoad,
	...props
}: Omit<ComponentProps<typeof Image>, "children"> & {
	imgClassName?: string
	toolbarClassName?: string
	toolbarLeft?: (isError: boolean) => ReactElement<HTMLProps<unknown>>
	toolbarRight?: (isError: boolean) => ReactElement<HTMLProps<unknown>>
	children?: ReactNode
	errorMessage?: ReactNode
	onZoomUpdate?: (scale: number) => void
}) => {
	const [state, setState] = useState({ ...INITIAL_STATE, isError: !src })
	const leftToolbar = useMemo(
		() => toolbarLeft?.(state.isError),
		[state.isError, toolbarLeft],
	)
	const rightToolbar = useMemo(
		() => toolbarRight?.(state.isError),
		[state.isError, toolbarRight],
	)
	const handleReset = () => {
		setState(INITIAL_STATE)
	}
	const handleZoomIn = () => {
		if (state.isError) return

		setState((prev) => ({
			...prev,
			scale: Math.min(prev.scale + 1, MAX_SCALE),
		}))
	}
	const handleZoomOut = () => {
		if (state.isError) return

		setState((prev) => ({
			...prev,
			scale: Math.max(1, prev.scale - 1),
		}))
	}
	const handleWheel = (e: WheelEvent) => {
		if (state.isError) return

		const scaleAmount = e.deltaY > 0 ? 0.9 : 1.1
		setState((prev) => ({
			...prev,
			scale: Math.min(Math.max(prev.scale * scaleAmount, 1), MAX_SCALE),
		}))
	}
	const handleStart = (e: MouseEvent | TouchEvent) => {
		if ("touches" in e) {
			if (e.touches.length === 2) {
				const dist = getPinchDistance(e.touches)
				setState((prev) => ({
					...prev,
					pinchDistance: dist,
				}))
				return
			}
			const touch = e.touches[0]
			setState((prev) => ({
				...prev,
				dragging: true,
				lastX: touch.clientX,
				lastY: touch.clientY,
			}))
		} else {
			setState((prev) => ({
				...prev,
				dragging: true,
				lastX: e.clientX,
				lastY: e.clientY,
			}))
		}
	}
	const handleMove = (e: MouseEvent | TouchEvent) => {
		if (state.isError) return

		if ("touches" in e) {
			if (e.touches.length === 2 && state.pinchDistance !== null) {
				const newDist = getPinchDistance(e.touches)
				const scaleChange = newDist / state.pinchDistance

				setState((prev) => ({
					...prev,
					scale: Math.min(Math.max(prev.scale * scaleChange, 1), MAX_SCALE),
					pinchDistance: newDist,
				}))
				return
			}

			if (!state.dragging || e.touches.length !== 1) return

			const touch = e.touches[0]
			setState((prev) => ({
				...prev,
				translateX: prev.translateX + (touch.clientX - prev.lastX),
				translateY: prev.translateY + (touch.clientY - prev.lastY),
				lastX: touch.clientX,
				lastY: touch.clientY,
			}))
		} else {
			if (!state.dragging) return

			setState((prev) => ({
				...prev,
				translateX: prev.translateX + (e.clientX - prev.lastX),
				translateY: prev.translateY + (e.clientY - prev.lastY),
				lastX: e.clientX,
				lastY: e.clientY,
			}))
		}
	}
	const handleEnd = () => {
		setState((prev) => ({
			...prev,
			dragging: false,
			pinchDistance: null,
		}))
	}

	useHotkeys(["shift+Equal", "NumpadAdd"], handleZoomIn)
	useHotkeys(["Minus", "NumpadSubtract"], handleZoomOut)
	useHotkeys("0", handleReset)

	useEffect(() => onZoomUpdate?.(state.scale), [onZoomUpdate, state.scale])

	return (
		<div
			className={cn(
				"relative cursor-grab touch-none overflow-hidden bg-black active:cursor-grabbing",
				className,
			)}
			onWheel={handleWheel}
			onMouseDown={handleStart}
			onMouseMove={handleMove}
			onMouseUp={handleEnd}
			onMouseLeave={handleEnd}
			onTouchStart={handleStart}
			onTouchMove={handleMove}
			onTouchEnd={handleEnd}
			onDoubleClick={handleZoomIn}
		>
			{state.isError && errorMessage && (
				<div className="flex h-full flex-col items-center justify-center gap-2">
					<WeightIcon.Null
						className="size-30 text-navy-60"
						aria-label="No image available"
					/>
					<p className="text-center text-xl text-white">{errorMessage}</p>
				</div>
			)}

			<Image
				{...props}
				src={src}
				draggable={false}
				rootClassName={cn("w-full", state.isError && "hidden")}
				className={cn(
					"grab origin-center touch-none transition-transform",
					imgClassName,
				)}
				style={{
					transform: `translate(${state.translateX}px, ${state.translateY}px) scale(${state.scale})`,
				}}
				onError={() => setState((prev) => ({ ...prev, isError: true }))}
				onLoad={(event) => {
					setState((prev) => ({ ...prev, isError: false }))
					onLoad?.(event)
				}}
			/>
			<div
				className={cn(
					"absolute top-0 left-1/2 flex w-full -translate-x-1/2 justify-center gap-2 px-6 pt-6",
					toolbarClassName,
				)}
			>
				{leftToolbar &&
					cloneElement(leftToolbar, {
						...leftToolbar.props,
						onWheel: onWheelStopPropagation,
					})}

				{!state.isError && (
					<div className="flex gap-5" onWheel={onWheelStopPropagation}>
						<Tooltip.Dark
							title={<ShortcutLabel label="Reset zoom" shortcut="0" />}
						>
							<Button
								variant="filled"
								color="default"
								size="large"
								aria-label="Reset zoom"
								icon={<UndoOutlined aria-hidden />}
								onClick={handleReset}
							/>
						</Tooltip.Dark>
						<Tooltip.Dark
							title={<ShortcutLabel label="Zoom out" shortcut="-" />}
						>
							<Button
								variant="filled"
								color="default"
								size="large"
								aria-label="Zoom out"
								icon={<ZoomOutOutlined aria-hidden />}
								onClick={handleZoomOut}
							/>
						</Tooltip.Dark>
						<Tooltip.Dark
							title={<ShortcutLabel label="Zoom in" shortcut="+" />}
						>
							<Button
								variant="filled"
								color="default"
								size="large"
								aria-label="Zoom in"
								icon={<ZoomInOutlined aria-hidden />}
								onClick={handleZoomIn}
							/>
						</Tooltip.Dark>
					</div>
				)}

				{rightToolbar &&
					cloneElement(rightToolbar, {
						...rightToolbar.props,
						onWheel: onWheelStopPropagation,
					})}
			</div>

			{Children.map(children, (child) => {
				if (!isValidElement(child)) {
					return null
				}
				type ChildCast = ReactElement<HTMLProps<unknown>>
				return cloneElement(child as ChildCast, {
					...(child as ChildCast).props,
					onWheel: onWheelStopPropagation,
				})
			})}
		</div>
	)
}

function onWheelStopPropagation(e: WheelEvent<HTMLElement>) {
	e.stopPropagation()
}

function getPinchDistance(touches: TouchList) {
	const [touch1, touch2] = [touches[0], touches[1]]
	const dx = touch2.clientX - touch1.clientX
	const dy = touch2.clientY - touch1.clientY
	return Math.sqrt(dx * dx + dy * dy)
}

const INITIAL_STATE = {
	isError: false,
	scale: 0.9,
	translateX: 0,
	translateY: 35,
	dragging: false,
	lastX: 0,
	lastY: 0,
	pinchDistance: null as number | null,
}

const MAX_SCALE = 10
